import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext.jsx";
import { getProductHighlight } from "@/api/public.api.js";
import ProductHighlightPopup from "@/components/ProductHighlightPopup.jsx";
import { MAIN_ROUTE, ROLES } from "@/utils/constants.js";
import {
    isProductHighlightPopupEligible,
    markProductHighlightPopupShown,
    productHighlightPopupScheduleKey,
    readProductHighlightPopupState
} from "@/utils/productHighlightPopupStorage.js";

/**
 * Avoid scheduling the same localStorage eligibility window twice (React Strict Mode / remounts).
 * Resets when a new cooldown window becomes eligible.
 */
let scheduledForCooldownKey = null;

async function fetchHighlightPreferringDifferent(lastProductId) {
    const first = await getProductHighlight();
    const firstId = first?.product?.id != null ? String(first.product.id) : null;
    if (!firstId || !lastProductId || firstId !== lastProductId) {
        return first;
    }
    // Same as last shown — try once more so another candidate can win when the pool has more than one.
    const second = await getProductHighlight();
    const secondId = second?.product?.id != null ? String(second.product.id) : null;
    if (secondId && secondId !== lastProductId) {
        return second;
    }
    // Only one candidate (or random hit the same twice) — show it after the cooldown gap.
    return first;
}

/**
 * Catalogue-only highlight on `/`.
 * First eligible visit shows a popup; then a 15-minute browser cooldown (localStorage)
 * blocks further API calls until it elapses. Deferred while walkthrough or login/register is open.
 */
export function useProductHighlightPopup({ walkthroughActive = false, loginOpen = false } = {}) {
    const { isAuthenticated, role } = useAuth();
    const location = useLocation();
    const isAdmin = isAuthenticated && role === ROLES.ADMIN;
    const onCatalogue = location.pathname === MAIN_ROUTE;

    const [payload, setPayload] = useState(null);
    const timerRef = useRef(null);

    const close = useCallback(() => {
        setPayload(null);
    }, []);

    useEffect(() => {
        if (!onCatalogue) {
            setPayload(null);
            return undefined;
        }

        if (isAdmin || walkthroughActive || loginOpen) {
            return undefined;
        }

        const state = readProductHighlightPopupState();
        if (!isProductHighlightPopupEligible()) {
            return undefined;
        }

        const scheduleKey = productHighlightPopupScheduleKey(state);
        if (scheduledForCooldownKey === scheduleKey) {
            return undefined;
        }
        scheduledForCooldownKey = scheduleKey;

        let cancelled = false;
        let finished = false;
        timerRef.current = window.setTimeout(async () => {
            try {
                const lastProductId = readProductHighlightPopupState()?.lastProductId ?? null;
                const response = await fetchHighlightPreferringDifferent(lastProductId);
                if (cancelled) {
                    return;
                }
                finished = true;
                if (response?.product && response?.kind) {
                    const productId =
                        response.product.id != null ? String(response.product.id) : null;
                    markProductHighlightPopupShown({ productId });
                    setPayload({ kind: response.kind, product: response.product });
                } else if (scheduledForCooldownKey === scheduleKey) {
                    // No candidate — allow a later catalogue visit in this session to try again
                    scheduledForCooldownKey = null;
                }
            } catch {
                // Catalogue stays usable without the popup
                if (!cancelled) {
                    finished = true;
                    if (scheduledForCooldownKey === scheduleKey) {
                        scheduledForCooldownKey = null;
                    }
                }
            }
        }, 1200);

        return () => {
            cancelled = true;
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            // Allow React Strict Mode remount (or deferred walkthrough) to schedule again.
            if (!finished && scheduledForCooldownKey === scheduleKey) {
                scheduledForCooldownKey = null;
            }
        };
    }, [onCatalogue, isAdmin, walkthroughActive, loginOpen]);

    const ProductHighlightHost = useCallback(() => {
        if (!onCatalogue || !payload?.product || !payload?.kind) {
            return null;
        }
        return (
            <ProductHighlightPopup
                kind={payload.kind}
                product={payload.product}
                onClose={close}
            />
        );
    }, [onCatalogue, payload, close]);

    return { ProductHighlightHost, highlightOpen: Boolean(payload) };
}
